import { type ReactNode } from 'react'
import { FormGroupContainer } from '@/components/FormContainer'
import { FormRow } from '@/components/Form'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
  ModalSheetSection,
  ModalSheetTitle,
} from '@/components/ModalSheet'
import { Button, RawButton } from '@/components/Button'
import {
  RadioButtonGroup,
  RadioButtonItem,
} from '@/components/RadioButtonGroup'
import type { Id } from '@/infra/id'
import type {
  InventoryItemPricing,
  InventoryItemType,
} from '@/features/inventory/InventoryItem'
import styles from './InventoryItemForm.module.css'
import cx from 'classnames'

export interface InventoryItemFormProps {
  showing: boolean
  onClose: () => unknown
  title: string
  submitText: string
  isSubmitPending: boolean
  /**
   * The item form, submitted by the footer's submit button via `form="addItemForm"`.
   */
  children?: ReactNode
  /**
   * The nested modals opened from within the form.
   */
  modals?: ReactNode
  testId?: string
}

export function InventoryItemForm({
  showing,
  onClose,
  title,
  submitText,
  isSubmitPending,
  children,
  modals,
  testId,
}: InventoryItemFormProps) {
  return (
    <ModalSheet
      disableCloseOnOutsideClick
      showing={showing}
      onRequestClose={isSubmitPending ? undefined : onClose}
    >
      <ModalSheetHeader bordered withCloseButton>
        <ModalSheetTitle>{title}</ModalSheetTitle>
      </ModalSheetHeader>
      <ModalSheetContent>
        <ModalSheetSection spaced>{children}</ModalSheetSection>
      </ModalSheetContent>

      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button
            label="Close"
            testId={testId ? `${testId}-close-button` : undefined}
            isDisabled={isSubmitPending}
            onPress={onClose}
            variant="default"
          />

          <Button
            label={submitText}
            type="submit"
            form="addItemForm"
            variant="primary"
            isPending={isSubmitPending}
            isDisabled={isSubmitPending}
            testId={testId ? `${testId}-submit-button` : undefined}
          />
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
      {modals}
    </ModalSheet>
  )
}

export interface InventoryItemTypeFieldProps {
  isDefault: boolean
  isTax: boolean
  isBookkeeping: boolean
  isConsultation: boolean
  isExtension: boolean
  isDisabled?: boolean
  onItemTypeChange: (itemType: InventoryItemType) => void
  testId?: string
}

export function InventoryItemTypeField({
  isDefault,
  isTax,
  isBookkeeping,
  isConsultation,
  isExtension,
  isDisabled,
  onItemTypeChange,
  testId,
}: InventoryItemTypeFieldProps) {
  return (
    <FormRow>
      <FormGroupContainer label="Service type">
        <RadioButtonGroup size="big-wide">
          <RadioButtonItem
            onPress={() => onItemTypeChange('Default')}
            active={isDefault}
            isDisabled={isDisabled}
            testId={testId ? `${testId}-type-default-button` : undefined}
          >
            Default
          </RadioButtonItem>
          <RadioButtonItem
            onPress={() => onItemTypeChange('Tax')}
            active={isTax}
            isDisabled={isDisabled}
            testId={testId ? `${testId}-type-tax-button` : undefined}
          >
            Tax
          </RadioButtonItem>
          <RadioButtonItem
            onPress={() => onItemTypeChange('Bookkeeping')}
            active={isBookkeeping}
            isDisabled={isDisabled}
            testId={testId ? `${testId}-type-bookkeeping-button` : undefined}
          >
            Bookkeeping
          </RadioButtonItem>
          <RadioButtonItem
            onPress={() => onItemTypeChange('Consultation')}
            active={isConsultation}
            isDisabled={isDisabled}
            testId={testId ? `${testId}-type-consultation-button` : undefined}
          >
            Consultation
          </RadioButtonItem>
          <RadioButtonItem
            onPress={() => onItemTypeChange('Extension')}
            active={isExtension}
            isDisabled={isDisabled}
            testId={testId ? `${testId}-type-extension-button` : undefined}
          >
            Extension
          </RadioButtonItem>
        </RadioButtonGroup>
      </FormGroupContainer>
    </FormRow>
  )
}

export interface InventoryItemPriceTypeFieldProps {
  isFlatPriceType: boolean
  itemPriceType: InventoryItemPricing
  requiresFlatPricing: boolean
  onItemPriceTypeChange: (priceType: InventoryItemPricing) => void
  testId?: string
}

export function InventoryItemPriceTypeField({
  isFlatPriceType,
  itemPriceType,
  requiresFlatPricing,
  onItemPriceTypeChange,
  testId,
}: InventoryItemPriceTypeFieldProps) {
  return (
    <FormRow>
      <FormGroupContainer label={'Price type'}>
        <RadioButtonGroup>
          <RadioButtonItem
            onPress={() => onItemPriceTypeChange('Flat')}
            active={isFlatPriceType}
            testId={testId ? `${testId}-price-type-flat-button` : undefined}
          >
            Flat
          </RadioButtonItem>
          <RadioButtonItem
            onPress={() => onItemPriceTypeChange('Fee')}
            active={itemPriceType === 'Fee'}
            isDisabled={requiresFlatPricing}
            testId={testId ? `${testId}-price-type-fee-button` : undefined}
          >
            Fee
          </RadioButtonItem>
          <RadioButtonItem
            onPress={() => onItemPriceTypeChange('Markup')}
            active={itemPriceType === 'Markup'}
            isDisabled={requiresFlatPricing}
            testId={testId ? `${testId}-price-type-markup-button` : undefined}
          >
            Markup
          </RadioButtonItem>
          <RadioButtonItem
            onPress={() => onItemPriceTypeChange('Markdown')}
            active={itemPriceType === 'Markdown'}
            isDisabled={requiresFlatPricing}
            testId={testId ? `${testId}-price-type-markdown-button` : undefined}
          >
            Markdown
          </RadioButtonItem>
        </RadioButtonGroup>
      </FormGroupContainer>
    </FormRow>
  )
}

export interface InventoryItemSupportingItemRow {
  key: Id
  label: string
  required: boolean
}

export interface InventoryItemSupportingItemsFieldProps {
  supportingItems: InventoryItemSupportingItemRow[]
  onManage: () => void
}

export function InventoryItemSupportingItemsField({
  supportingItems,
  onManage,
}: InventoryItemSupportingItemsFieldProps) {
  return (
    <FormRow>
      <FormGroupContainer>
        <div className={styles.supportingItemsLabel}>
          <div>
            Supporting items
            <span className={styles.supportingItemCount}>
              ({supportingItems.length})
            </span>
          </div>
          <RawButton
            className={styles.supportingItemRowManage}
            onPress={onManage}
          >
            Manage
          </RawButton>
        </div>
        <div>
          {supportingItems.length === 0 && <NoSupportingItems />}
          {supportingItems.map((item) => (
            <SupportingItem key={item.key} supportingItem={item} />
          ))}
        </div>
      </FormGroupContainer>
    </FormRow>
  )
}

interface SupportingItemProps {
  supportingItem: InventoryItemSupportingItemRow
}

function SupportingItem({ supportingItem }: SupportingItemProps) {
  return (
    <div className={styles.supportingItem}>
      {supportingItem.label}
      {supportingItem.required && (
        <span className={styles.required}>* required</span>
      )}
    </div>
  )
}

function NoSupportingItems() {
  return (
    <div className={cx(styles.supportingItem, styles.noneAssigned)}>
      None assigned
    </div>
  )
}
